import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import './MessagesPage.css';

const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({ received: [], sent: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('messages'); // messages, friends, requests, suggestions
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [addFriendEmail, setAddFriendEmail] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedFriend) {
      loadMessages(selectedFriend.Id || selectedFriend.id);
    }
  }, [selectedFriend]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      const [convRes, friendsRes, pendingRes, suggestionsRes] = await Promise.all([
        api.get('/messages/conversations'),
        api.get('/friends'),
        api.get('/friends/pending'),
        api.get('/friends/suggestions')
      ]);
      setConversations(Array.isArray(convRes.data) ? convRes.data : []);
      setFriends(Array.isArray(friendsRes.data) ? friendsRes.data : []);
      setPendingRequests(pendingRes.data || { received: [], sent: [] });
      setSuggestions(Array.isArray(suggestionsRes.data) ? suggestionsRes.data : []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (friendId) => {
    try {
      const res = await api.get(`/messages/${friendId}`);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      toast.error('Erro ao carregar mensagens');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedFriend) return;

    try {
      const friendId = selectedFriend.Id || selectedFriend.id;
      const res = await api.post(`/messages/${friendId}`, { content: newMessage });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    }
  };

  const handleAddFriend = async (e) => {
    e.preventDefault();
    if (!addFriendEmail.trim()) return;

    try {
      await api.post('/friends/request', { email: addFriendEmail });
      toast.success('Pedido de amizade enviado!');
      setAddFriendEmail('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao enviar pedido');
    }
  };

  const handleAddFriendByEmail = async (email) => {
    try {
      await api.post('/friends/request', { email });
      toast.success('Pedido de amizade enviado!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao enviar pedido');
    }
  };

  const handleAcceptRequest = async (id) => {
    try {
      await api.post(`/friends/${id}/accept`);
      toast.success('Pedido aceite!');
      loadData();
    } catch (error) {
      toast.error('Erro ao aceitar pedido');
    }
  };

  const handleRejectRequest = async (id) => {
    try {
      await api.post(`/friends/${id}/reject`);
      toast.success('Pedido rejeitado');
      loadData();
    } catch (error) {
      toast.error('Erro ao rejeitar pedido');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!window.confirm('Tem certeza que deseja remover este amigo?')) return;
    
    try {
      await api.delete(`/friends/${friendId}`);
      toast.success('Amigo removido');
      loadData();
      if (selectedFriend && (selectedFriend.Id === friendId || selectedFriend.id === friendId)) {
        setSelectedFriend(null);
      }
    } catch (error) {
      toast.error('Erro ao remover amigo');
    }
  };

  const startConversation = (friend) => {
    setSelectedFriend(friend);
    setActiveTab('messages');
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.UnreadCount || c.unreadCount || 0), 0);
  const pendingCount = (pendingRequests.received?.length || 0);

  if (loading) {
    return (
      <DashboardLayout userType="client">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>A carregar...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client">
      <div className="messages-page">
        <div className="messages-container">
          {/* Sidebar */}
          <div className="messages-sidebar">
            <div className="sidebar-tabs">
              <button 
                className={`sidebar-tab ${activeTab === 'messages' ? 'active' : ''}`}
                onClick={() => setActiveTab('messages')}
              >
                💬 Mensagens
                {totalUnread > 0 && <span className="tab-badge">{totalUnread}</span>}
              </button>
              <button 
                className={`sidebar-tab ${activeTab === 'friends' ? 'active' : ''}`}
                onClick={() => setActiveTab('friends')}
              >
                👥 Amigos ({friends.length})
              </button>
              <button 
                className={`sidebar-tab ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                📩 Pedidos
                {pendingCount > 0 && <span className="tab-badge">{pendingCount}</span>}
              </button>
              <button 
                className={`sidebar-tab ${activeTab === 'suggestions' ? 'active' : ''}`}
                onClick={() => setActiveTab('suggestions')}
              >
                ✨ Sugestões
                {suggestions.length > 0 && <span className="tab-badge">{suggestions.length}</span>}
              </button>
            </div>

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="conversations-list">
                {conversations.length === 0 ? (
                  <div className="empty-sidebar">
                    <p>Sem conversas</p>
                    <button className="btn btn-primary btn-sm" onClick={() => setActiveTab('friends')}>
                      Ver Amigos
                    </button>
                  </div>
                ) : (
                  conversations.map(conv => (
                    <div 
                      key={conv.FriendId || conv.friendId}
                      className={`conversation-item ${selectedFriend && (selectedFriend.Id === conv.FriendId || selectedFriend.id === conv.friendId) ? 'active' : ''}`}
                      onClick={() => startConversation({ ...(conv.Friend || conv.friend), Id: conv.FriendId || conv.friendId })}
                    >
                      <div className="conversation-avatar">
                        {(conv.Friend?.Name || conv.friend?.name)?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="conversation-info">
                        <span className="conversation-name">{conv.Friend?.Name || conv.friend?.name}</span>
                        <span className="conversation-preview">
                          {conv.LastMessage?.Content || conv.lastMessage?.content || 'Iniciar conversa'}
                        </span>
                      </div>
                      {(conv.UnreadCount || conv.unreadCount) > 0 && (
                        <span className="unread-badge">{conv.UnreadCount || conv.unreadCount}</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Friends Tab */}
            {activeTab === 'friends' && (
              <div className="friends-list">
                <form onSubmit={handleAddFriend} className="add-friend-form">
                  <input
                    type="email"
                    placeholder="Email do amigo..."
                    value={addFriendEmail}
                    onChange={(e) => setAddFriendEmail(e.target.value)}
                  />
                  <button type="submit" className="btn btn-primary btn-sm">+</button>
                </form>
                
                {friends.length === 0 ? (
                  <div className="empty-sidebar">
                    <p>Sem amigos ainda</p>
                    <p className="hint">Adicione amigos pelo email</p>
                  </div>
                ) : (
                  friends.map(f => (
                    <div key={f.Friend?.Id || f.friend?.id} className="friend-item">
                      <div className="friend-avatar">
                        {(f.Friend?.Name || f.friend?.name)?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="friend-info">
                        <span className="friend-name">{f.Friend?.Name || f.friend?.name}</span>
                        <span className="friend-email">{f.Friend?.Email || f.friend?.email}</span>
                      </div>
                      <div className="friend-actions">
                        <button 
                          className="btn-icon" 
                          onClick={() => startConversation(f.Friend || f.friend)}
                          title="Enviar mensagem"
                        >
                          💬
                        </button>
                        <button 
                          className="btn-icon danger" 
                          onClick={() => handleRemoveFriend(f.Friend?.Id || f.friend?.id)}
                          title="Remover amigo"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="requests-list">
                <h4>Pedidos Recebidos</h4>
                {pendingRequests.received?.length === 0 ? (
                  <p className="empty-text">Sem pedidos pendentes</p>
                ) : (
                  pendingRequests.received?.map(req => (
                    <div key={req.Id || req.id} className="request-item">
                      <div className="request-avatar">
                        {(req.User?.Name || req.user?.name)?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="request-info">
                        <span className="request-name">{req.User?.Name || req.user?.name}</span>
                        <span className="request-email">{req.User?.Email || req.user?.email}</span>
                      </div>
                      <div className="request-actions">
                        <button 
                          className="btn btn-success btn-xs"
                          onClick={() => handleAcceptRequest(req.Id || req.id)}
                        >
                          ✓
                        </button>
                        <button 
                          className="btn btn-danger btn-xs"
                          onClick={() => handleRejectRequest(req.Id || req.id)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))
                )}

                <h4 style={{ marginTop: '20px' }}>Pedidos Enviados</h4>
                {pendingRequests.sent?.length === 0 ? (
                  <p className="empty-text">Sem pedidos enviados</p>
                ) : (
                  pendingRequests.sent?.map(req => (
                    <div key={req.Id || req.id} className="request-item sent">
                      <div className="request-avatar">
                        {(req.User?.Name || req.user?.name)?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="request-info">
                        <span className="request-name">{req.User?.Name || req.user?.name}</span>
                        <span className="request-status">Aguardando...</span>
                      </div>
                      <button 
                        className="btn btn-outline btn-xs"
                        onClick={() => handleRejectRequest(req.Id || req.id)}
                      >
                        Cancelar
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Suggestions Tab */}
            {activeTab === 'suggestions' && (
              <div className="suggestions-list">
                <div className="suggestions-header">
                  <p className="suggestions-info">
                    💡 Pessoas que treinam nos mesmos horários que tu
                  </p>
                </div>
                {suggestions.length === 0 ? (
                  <div className="empty-sidebar">
                    <p>Sem sugestões</p>
                    <p className="hint">Reserva mais aulas para descobrir pessoas com horários semelhantes</p>
                  </div>
                ) : (
                  suggestions.map(s => (
                    <div key={s.Id || s.id} className="suggestion-item">
                      <div className="suggestion-avatar">
                        {(s.Name || s.name)?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="suggestion-info">
                        <span className="suggestion-name">{s.Name || s.name}</span>
                        <span className="suggestion-common">
                          🏋️ {s.CommonWorkouts || s.commonWorkouts} treinos em comum
                        </span>
                      </div>
                      <button 
                        className="btn btn-primary btn-xs"
                        onClick={() => handleAddFriendByEmail(s.Email || s.email)}
                      >
                        Adicionar
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Chat Area */}
          <div className="chat-area">
            {!selectedFriend ? (
              <div className="no-conversation">
                <div className="no-conversation-icon">💬</div>
                <h3>Selecione uma conversa</h3>
                <p>Escolha um amigo para começar a conversar</p>
              </div>
            ) : (
              <>
                <div className="chat-header">
                  <button className="back-btn" onClick={() => setSelectedFriend(null)}>
                    ←
                  </button>
                  <div className="chat-header-avatar">
                    {(selectedFriend.Name || selectedFriend.name)?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="chat-header-info">
                    <span className="chat-header-name">{selectedFriend.Name || selectedFriend.name}</span>
                    <span className="chat-header-status">Online</span>
                  </div>
                </div>

                <div className="messages-list">
                  {messages.length === 0 ? (
                    <div className="no-messages">
                      <p>Sem mensagens ainda</p>
                      <p>Envie a primeira mensagem!</p>
                    </div>
                  ) : (
                    messages.map(msg => (
                      <div 
                        key={msg.Id || msg.id} 
                        className={`message ${msg.IsFromMe || msg.isFromMe ? 'sent' : 'received'}`}
                      >
                        <div className="message-content">
                          {msg.Content || msg.content}
                        </div>
                        <div className="message-time">
                          {format(new Date(msg.SentAt || msg.sentAt), 'HH:mm', { locale: pt })}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form className="message-input-form" onSubmit={sendMessage}>
                  <input
                    type="text"
                    placeholder="Escreva uma mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    maxLength={2000}
                  />
                  <button type="submit" className="send-btn" disabled={!newMessage.trim()}>
                    Enviar
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MessagesPage;
